import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  yangoApi,
  type YangoDispatchListParams,
  type YangoDispatchResponse,
} from "@/lib/admin-api/yango";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const YANGO_KEY = "admin-yango-dispatches";

function yangoQueryKey(params: YangoDispatchListParams) {
  return [YANGO_KEY, params] as const;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Fetch paginated + filtered Yango dispatches list. */
export function useYangoDispatches(params: YangoDispatchListParams) {
  return useQuery({
    queryKey: yangoQueryKey(params),
    queryFn: () => yangoApi.list(params),
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

/** Dispatch a single order to Yango and invalidate the list cache. */
export function useYangoDispatch() {
  const queryClient = useQueryClient();

  return useMutation<YangoDispatchResponse, Error, { orderId: string }>({
    mutationFn: ({ orderId }) => yangoApi.dispatch(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [YANGO_KEY] });
    },
  });
}

/** Cancel a pending Yango dispatch. */
export function useYangoCancelDispatch() {
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean }, Error, { dispatchId: string }>({
    mutationFn: ({ dispatchId }) => yangoApi.cancel(dispatchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [YANGO_KEY] });
    },
  });
}
